import { create } from 'zustand';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'employee' | 'manager';
  department: string;
  createdAt: string;
}

interface Activity {
  id: string;
  buttonId: string;
  label: string;
  timestamp: string;
  category: string;
}

interface Status {
  location: 'home' | 'office' | 'remote' | 'traveling';
  availability: 'available' | 'busy' | 'meeting' | 'break' | 'focus' | 'offline';
  lastUpdated: string;
}

interface WorkStore {
  // User
  user: User | null;
  setUser: (user: User | null) => void;
  
  // Theme
  theme: 'light' | 'dark';
  setTheme: (theme: 'light' | 'dark') => void;
  
  // Status
  status: Status;
  updateStatus: (key: keyof Status, value: any) => void;
  
  // Activities
  activities: Activity[];
  addActivity: (activity: Activity) => void;
  clearActivities: () => void;
  
  // Analytics
  getActivityStats: () => {
    total: number;
    byCategory: Record<string, number>;
    today: number;
    thisWeek: number;
    mostUsed: string[];
  };
  
  // Settings
  notifications: boolean;
  soundEffects: boolean;
  autoStatus: boolean;
  toggleSetting: (setting: 'notifications' | 'soundEffects' | 'autoStatus') => void;
}

export const useWorkStore = create<WorkStore>((set, get) => ({
  // User
  user: null,
  setUser: (user) => set({ user }),
  
  // Theme
  theme: 'light',
  setTheme: (theme) => {
    set({ theme });
    localStorage.setItem('workbuttons_theme', theme);
  },
  
  // Status
  status: {
    location: 'office',
    availability: 'available',
    lastUpdated: new Date().toISOString(),
  },
  updateStatus: (key, value) => set((state) => ({
    status: {
      ...state.status,
      [key]: value,
      lastUpdated: new Date().toISOString(),
    }
  })),
  
  // Activities
  activities: [],
  addActivity: (activity) => set((state) => {
    const newActivities = [activity, ...state.activities].slice(0, 100); // Keep last 100
    localStorage.setItem('workbuttons_activities', JSON.stringify(newActivities));
    return { activities: newActivities };
  }),
  clearActivities: () => {
    localStorage.removeItem('workbuttons_activities');
    set({ activities: [] });
  },
  
  // Analytics
  getActivityStats: () => {
    const activities = get().activities;
    const now = new Date();
    const todayStart = new Date(now.setHours(0, 0, 0, 0));
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
    
    const byCategory = activities.reduce((acc, activity) => {
      acc[activity.category] = (acc[activity.category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const buttonCounts = activities.reduce((acc, activity) => {
      acc[activity.buttonId] = (acc[activity.buttonId] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const mostUsed = Object.entries(buttonCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([buttonId]) => buttonId);
    
    return {
      total: activities.length,
      byCategory,
      today: activities.filter(a => new Date(a.timestamp) >= todayStart).length,
      thisWeek: activities.filter(a => new Date(a.timestamp) >= weekStart).length,
      mostUsed,
    };
  },
  
  // Settings
  notifications: true,
  soundEffects: true,
  autoStatus: false,
  toggleSetting: (setting) => set((state) => ({
    [setting]: !state[setting]
  })),
}));

// Initialize from localStorage
if (typeof window !== 'undefined') {
  const savedTheme = localStorage.getItem('workbuttons_theme');
  if (savedTheme) {
    useWorkStore.setState({ theme: savedTheme as 'light' | 'dark' });
  }
  
  const savedActivities = localStorage.getItem('workbuttons_activities');
  if (savedActivities) {
    try {
      useWorkStore.setState({ activities: JSON.parse(savedActivities) });
    } catch (e) {
      console.error('Failed to load activities:', e);
    }
  }
}